import { SearchableCandidateFilterInput } from 'generated-graphql/types';

type SearchFieldHandler = (
  value: string,
  andConditions: SearchableCandidateFilterInput[]
) => void;

type FilterValueHandler = (
  items: Array<{ label: string; value: string }>,
  andConditions: SearchableCandidateFilterInput[]
) => void;

export const buildFullNameFilter = (
  value: string,
  andConditions: SearchableCandidateFilterInput[]
) => {
  const fullName = value.split(' ');
  if (!fullName[1]) {
    andConditions.push({
      or: [
        { firstName: { matchPhrasePrefix: value } },
        { lastName: { matchPhrasePrefix: value } }
      ]
    });
  } else {
    andConditions.push({
      and: [
        { firstName: { matchPhrasePrefix: fullName[0] } },
        { lastName: { matchPhrasePrefix: fullName[1] } }
      ]
    });
  }
};

export const buildAtsApplicationSourceFilter = (
  value: string,
  andConditions: SearchableCandidateFilterInput[]
) => {
  andConditions.push({
    atsApplicationSource: { matchPhrasePrefix: value }
  });
};

export const buildStatusFilter = (
  items: Array<{ label: string; value: string }>,
  andConditions: SearchableCandidateFilterInput[]
) => {
  if (items.length === 1) {
    andConditions.push({ status: { eq: items[0].value } });
  } else {
    andConditions.push({
      or: items.map((item) => ({ status: { eq: item.value } }))
    });
  }
};

export const buildOriginFilter = (
  items: Array<{ label: string; value: string }>,
  andConditions: SearchableCandidateFilterInput[]
) => {
  if (items.length === 1) {
    andConditions.push({ origin: { eq: items[0].value } });
  } else {
    andConditions.push({
      or: items.map((item) => ({ origin: { eq: item.value } }))
    });
  }
};

type BuildSearchableCandidateFiltersProps<T extends string> = {
  searchFields?: Partial<Record<T, string>>;
  filterValues?: Partial<Record<T, Array<{ label: string; value: string }>>>;
  searchFieldHandlers: Partial<Record<T, SearchFieldHandler>>;
  filterValueHandlers: Partial<Record<T, FilterValueHandler>>;
  baseFilter: SearchableCandidateFilterInput;
  shouldTrimSearchFields?: boolean;
};

export const buildSearchableCandidateFilters = <T extends string>({
  searchFields,
  filterValues,
  searchFieldHandlers,
  filterValueHandlers,
  baseFilter,
  shouldTrimSearchFields = false
}: BuildSearchableCandidateFiltersProps<T>): SearchableCandidateFilterInput => {
  const filterObject = {} as SearchableCandidateFilterInput;
  const andConditions: SearchableCandidateFilterInput[] = [];

  if (searchFields) {
    Object.entries(searchFields).forEach(([key, value]) => {
      const searchValue = value as string;
      if (
        shouldTrimSearchFields &&
        (!searchValue || searchValue.trim() === '')
      ) {
        return;
      }

      const handler = searchFieldHandlers[key as T];
      if (handler) {
        handler(searchValue, andConditions);
      }
    });
  }

  if (filterValues) {
    Object.entries(filterValues).forEach(([key, items]) => {
      const filterItems = items as Array<{ label: string; value: string }>;
      if (filterItems && filterItems.length > 0) {
        const handler = filterValueHandlers[key as T];
        if (handler) {
          handler(filterItems, andConditions);
        }
      }
    });
  }

  if (andConditions.length > 0) {
    filterObject.and = andConditions;
  }

  return {
    and: [filterObject, baseFilter]
  };
};
